import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import PublicNavbar from "../components/PublicNavbar";
import Footer from "../components/Footer";
import { openInteractiveDemo } from "../components/demo/DemoLauncher";
import { CalendarMockup, ContractMockup, PaymentMockup } from "../components/BecomeHostMockups";
import {
  LayoutGrid, CalendarDays, FileText, PenLine, Camera, Wrench, Users, CreditCard,
  Settings2, ClipboardList, ArrowRight, Play, Gift, Sparkles,
  Globe, Package, Receipt, UserCog, MailCheck,
} from "lucide-react";

const OVERLINE = "text-xs font-bold tracking-[0.2em] uppercase text-violet-600";

const FEATURES = [
  {
    icon: Sparkles,
    title: "Junago Copilot (IA)",
    desc: "Un assistant IA qui analyse votre activité et vous recommande les bons ajustements.",
    slug: "assistant-ia",
  },
  {
    icon: CalendarDays,
    title: "Pilotage & calendrier",
    desc: "CA, réservations et indicateurs clés, plus un calendrier interactif véhicule par véhicule.",
    slug: "pilotage-calendrier",
  },
  {
    icon: Globe,
    title: "Site de réservation en ligne",
    desc: "Un site internet professionnel hébergé par Junago, synchronisé en temps réel avec votre logiciel.",
    slug: "page-de-reservation",
  },
  {
    icon: ClipboardList,
    title: "Réservations & devis",
    desc: "Créez des réservations en quelques secondes et transformez vos devis acceptés en un clic.",
    slug: "reservations-devis",
  },
  {
    icon: PenLine,
    title: "Contrats & signature électronique",
    desc: "Des contrats pré-remplis générés à chaque réservation, signés du bout du doigt.",
    slug: "contrats-signature",
  },
  {
    icon: Camera,
    title: "États des lieux digitaux",
    desc: "Photos horodatées, dommages annotés, comparaison départ/retour automatique.",
    slug: "etats-des-lieux",
  },
  {
    icon: Wrench,
    title: "Flotte, entretien & événements",
    desc: "Entretiens, alertes documents, sinistres, contraventions et fournisseurs au même endroit.",
    slug: "flotte-entretien",
  },
  {
    icon: Users,
    title: "CRM clients & prospects",
    desc: "Fiches clients complètes, documents, historique et scanner de permis intelligent.",
    slug: "crm-clients",
  },
  {
    icon: CreditCard,
    title: "Encaissements Stripe",
    desc: "Paiement en ligne 3D Secure, acomptes et virements automatiques sous 48h.",
    slug: "encaissements",
  },
  {
    icon: Package,
    title: "Produits & options",
    desc: "Sièges bébé, rachat de franchise, livraison : vendez des extras avec un stock maîtrisé par agence.",
    slug: "produits-options",
  },
  {
    icon: Receipt,
    title: "Revenus & facturation",
    desc: "Cockpit financier : factures et avoirs conformes, cautions, dépenses et export comptable.",
    slug: "revenus-facturation",
  },
  {
    icon: UserCog,
    title: "Équipe, rôles & sécurité",
    desc: "Permissions fines par module, restriction par agence et journal d'activité complet.",
    slug: "equipe-securite",
  },
  {
    icon: MailCheck,
    title: "Emails automatiques",
    desc: "Confirmations, rappels et documents envoyés au bon moment, avec vos modèles personnalisés.",
    slug: "emails-automatiques",
  },
  {
    icon: Settings2,
    title: "Paramétrages sur mesure",
    desc: "Statuts, tarifs saisonniers, alertes et catégories : Junago s'adapte à votre façon de travailler.",
    slug: "parametrages",
  },
];

const STATS = [
  { value: "÷4", label: "Temps administratif divisé par quatre grâce à l'automatisation" },
  { value: "90%", label: "Des tâches générées automatiquement, du contrat à la restitution" },
  { value: "100%", label: "Digitalisé : plus aucun papier, tout est archivé dans Junago" },
];

const DEEP_DIVES = [
  {
    overline: "Planning",
    title: "Toute votre flotte, sur un seul calendrier.",
    desc: "Chaque véhicule a sa ligne, chaque réservation son bloc coloré. Déplacez, prolongez, créez : le planning est votre poste de pilotage quotidien, pensé pour la location.",
    bullets: ["Vue jour / semaine / mois", "Tâches automatiques intégrées", "Détection des conflits de dates"],
    Mockup: CalendarMockup,
    reverse: false,
  },
  {
    overline: "Contrats",
    title: "Le contrat se remplit tout seul.",
    desc: "Dès qu'une réservation est confirmée, le contrat est généré avec les informations du client, du véhicule et de la location. Il ne reste qu'à faire signer — électroniquement.",
    bullets: ["Génération automatique", "Signature électronique horodatée", "Exemplaire envoyé à chaque partie"],
    Mockup: ContractMockup,
    reverse: true,
  },
  {
    overline: "Encaissements",
    title: "Encaissez par carte, dormez tranquille.",
    desc: "Vos clients paient par carte bancaire via Stripe, leader mondial du paiement sécurisé. Les fonds sont reversés directement sur votre compte, et vos revenus suivis en temps réel.",
    bullets: ["Toutes cartes, 3D-Secure inclus", "Virement automatique sur votre IBAN", "Suivi des revenus par véhicule"],
    Mockup: PaymentMockup,
    reverse: false,
  },
];

export default function Features() {
  useEffect(() => {
    document.title = "Fonctionnalités — Logiciel pour loueurs de véhicules | Junago";
  }, []);
  return (
    <div className="min-h-screen bg-white">
      <PublicNavbar />

      {/* Hero */}
      <section className="max-w-4xl mx-auto px-6 pt-14 md:pt-20 pb-12 text-center" data-testid="features-hero">
        <div className={`${OVERLINE} mb-4`}>Fonctionnalités</div>
        <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight leading-[1.05] text-slate-900" data-testid="features-title">
          Tout ce qu'il faut pour
          <br />
          <span className="text-violet-600">gérer vos locations</span>.
        </h1>
        <p className="mt-4 text-base md:text-lg text-slate-600 max-w-xl mx-auto leading-relaxed">
          Un logiciel pensé par des loueurs, pour des loueurs : chaque fonctionnalité vous fait gagner un temps précieux.
        </p>
        <div className="mt-7 flex flex-col sm:flex-row items-center justify-center gap-3">
          <Link
            to="/register?context=vendor"
            className="btn-primary inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-semibold"
            data-testid="features-cta-trial"
          >
            <Gift className="h-4 w-4" /> Essai gratuit 30 jours
          </Link>
          <button type="button" onClick={openInteractiveDemo} className="btn-jn-play-wrap" data-testid="features-cta-demo">
            <span className="btn-jn-play"><Play className="h-4 w-4 fill-current" /></span>
            <span className="btn-jn-play-label">Démo interactive</span>
          </button>
        </div>
      </section>

      {/* Stats strip */}
      <section className="bg-slate-950 text-white" data-testid="features-stats">
        <div className="max-w-6xl mx-auto px-6 py-12 grid sm:grid-cols-3 gap-8">
          {STATS.map((s) => (
            <div key={s.value} className="text-center sm:text-left">
              <div className="font-heading text-5xl font-medium tracking-tight text-violet-400">{s.value}</div>
              <p className="mt-2 text-sm text-slate-400 leading-relaxed max-w-[260px] mx-auto sm:mx-0">{s.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Feature grid */}
      <section className="max-w-6xl mx-auto px-6 py-16 md:py-24" data-testid="features-grid">
        <div className="mb-10">
          <div className={`${OVERLINE} mb-2`}>Vue d'ensemble</div>
          <h2 className="font-heading text-3xl md:text-4xl font-medium tracking-tight text-slate-900">
            Les fonctionnalités qui font gagner du temps
          </h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {FEATURES.map((f) => (
            <div
              key={f.title}
              className="group rounded-2xl border border-slate-200 bg-white p-7 hover:border-violet-300 hover:shadow-[0_12px_32px_-12px_rgba(124,58,237,0.25)] transition-all"
              data-testid={`feature-card-${f.title.slice(0, 12)}`}
            >
              <div className="h-11 w-11 rounded-xl bg-violet-50 flex items-center justify-center">
                <f.icon className="h-5 w-5 text-violet-600" />
              </div>
              <h3 className="mt-4 font-heading text-lg font-semibold text-slate-900 tracking-tight">{f.title}</h3>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed">{f.desc}</p>
              <Link
                to={`/fonctionnalites/${f.slug}`}
                className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-violet-600 hover:text-violet-800"
              >
                Découvrir <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
              </Link>
            </div>
          ))}
        </div>
      </section>

      {/* Deep dives */}
      <section className="bg-slate-50 border-y border-slate-200" data-testid="features-deep-dives">
        <div className="max-w-6xl mx-auto px-6 py-16 md:py-24 space-y-20 md:space-y-28">
          {DEEP_DIVES.map((d) => (
            <div key={d.title} className={`grid lg:grid-cols-2 gap-10 lg:gap-16 items-center`}>
              <div className={d.reverse ? "lg:order-2" : ""}>
                <div className={`${OVERLINE} mb-3`}>{d.overline}</div>
                <h3 className="font-heading text-3xl md:text-4xl font-medium tracking-tight text-slate-900 leading-tight">
                  {d.title}
                </h3>
                <p className="mt-4 text-slate-600 leading-relaxed">{d.desc}</p>
                <ul className="mt-5 space-y-2.5">
                  {d.bullets.map((b) => (
                    <li key={b} className="flex items-center gap-2.5 text-sm text-slate-700">
                      <span className="h-1.5 w-1.5 rounded-full bg-violet-600" /> {b}
                    </li>
                  ))}
                </ul>
              </div>
              <div className={`min-w-0 ${d.reverse ? "lg:order-1" : ""}`}>
                <d.Mockup />
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-5xl mx-auto px-6 py-16 md:py-24" data-testid="features-cta">
        <div className="relative overflow-hidden rounded-3xl bg-slate-950 text-white px-8 sm:px-14 py-14 text-center">
          <div aria-hidden className="absolute -left-20 -top-32 h-96 w-96 rounded-full bg-violet-600 opacity-25 blur-3xl" />
          <div className="relative">
            <h2 className="font-heading text-3xl md:text-4xl font-medium tracking-tight">
              Testez tout, gratuitement.
            </h2>
            <p className="mt-3 text-slate-400">30 jours d'essai complet, sans carte bancaire.</p>
            <div className="mt-7 flex flex-col sm:flex-row justify-center gap-3">
              <Link
                to="/register?context=vendor"
                className="btn-jn-light inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full text-sm font-semibold"
                data-testid="features-bottom-trial"
              >
                Commencer gratuitement <ArrowRight className="btn-arrow h-4 w-4" />
              </Link>
              <Link
                to="/tarifs"
                className="btn-jn-ghost-dark inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full text-sm font-semibold"
                data-testid="features-bottom-pricing"
              >
                Voir les tarifs
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
